import React, { FC } from "react";
import facebookSvg from "images/Facebook.svg";
import googleSvg from "images/Google.svg";
import { Helmet } from "react-helmet";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootStore } from "functions/redux/store";
import { getAuth, signInWithPopup, GoogleAuthProvider, FacebookAuthProvider, signInWithEmailAndPassword } from "firebase/auth";
import { checkLogin, emailLogin, getFullCreds, googleLogin, LOGIN_FALSE } from "functions/redux/user/action";
import Loader from "react-loader-spinner";
import NcModal from "shared/NcModal/NcModal";
import { modalContent } from "containers/PageSignUp/PageSignUp";
import { useTranslation } from "react-i18next";
import Badge from "shared/Badge/Badge";
import { getNotification } from "functions/redux/notification/action";
import { getRecentOpenTrips } from "functions/redux/listing/homeAction";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";

export interface PageLoginProps {
  className?: string;
}

const PageLogin: FC<PageLoginProps> = ({ className = "" }) => {
  const { user } = useSelector((state: RootStore) => state);
  const dispatch = useDispatch();
  const history = useHistory();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState("");
  const [modal, setModal] = React.useState(false);
  const [content, setContent] = React.useState(<div></div>);
  const { t } = useTranslation();

  React.useEffect(() => {
    if (user.credential == null) {
      dispatch(checkLogin());
    }
  }, [dispatch, user.credential]);

  React.useEffect(() => {
    if (user.credential != null && user.credential !== LOGIN_FALSE && user.credential.profile != null) {
      history.replace("/profile");
    }
  }, [user.credential, history]);

  async function loginWithGoogle() {
    if (loading === "") {
      setLoading('google');
      try {
        const provider = new GoogleAuthProvider();
        const auth = getAuth();
        const result = await signInWithPopup(auth, provider);

        const idToken = await result.user.getIdTokenResult();

        await dispatch(googleLogin(idToken.token));
        await dispatch(getFullCreds());
        await dispatch(getRecentOpenTrips());
        dispatch(getNotification());

        setLoading("");
        history.replace("/profile");
      } catch (err: any) {
        console.error("Google Login Error:", err);
        if (err?.code !== 'auth/popup-closed-by-user') {
          setContent(modalContent(t('an_error_occured')));
          setModal(true);
        }
        setLoading("");
      }
    }
  }

  async function loginWithFacebook() {
    if (loading === "") {
      setLoading('facebook');
      try {
        const provider = new FacebookAuthProvider();
        const auth = getAuth();
        const result = await signInWithPopup(auth, provider);

        const idToken = await result.user.getIdTokenResult();
        await dispatch(googleLogin(idToken.token));
        await dispatch(getFullCreds());
        await dispatch(getRecentOpenTrips());
        dispatch(getNotification());

        setLoading("");
        history.replace("/profile");
      } catch (err: any) {
        console.error("Facebook Login Error:", err);
        if (err?.code !== 'auth/popup-closed-by-user') {
          setContent(modalContent(t('an_error_occured')));
          setModal(true);
        }
        setLoading("");
      }
    }
  }

  async function loginWithEmail(e?: React.FormEvent) {
    if (e) e.preventDefault();
    if (!email || !password) {
      setContent(modalContent("Sila isi e-mel dan kata laluan"));
      setModal(true);
      return;
    }
    if (loading === "") {
      setLoading('email');
      try {
        const auth = getAuth();
        const result = await signInWithEmailAndPassword(auth, email, password);
        const idToken = await result.user.getIdTokenResult();
        await dispatch(emailLogin(idToken.token));
        await dispatch(getFullCreds());
        await dispatch(getNotification());
        setLoading("");
        history.replace("/profile");
      } catch (err: any) {
        console.error("Email Login Error:", err);
        let msg = t('an_error_occured');
        if (
          err?.code === 'auth/wrong-password' ||
          err?.code === 'auth/user-not-found' ||
          err?.code === 'auth/invalid-credential'
        ) {
          msg = "E-mel atau kata laluan tidak sah.";
        }
        setContent(modalContent(msg));
        setModal(true);
        setLoading("");
      }
    }
  }

  return (
    <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
      <NcModal
        isOpenProp={modal}
        renderTrigger={() => <div></div>}
        renderContent={() => content}
        onCloseModal={() => setModal(false)}
        modalTitle="Information"
      />
      <Helmet>
        <title>{t('login')}</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-12 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          GuideSantai
        </h2>
        <div className="max-w-md mx-auto space-y-6">
          <span className="block text-center text-base md:text-lg text-neutral-500 dark:text-neutral-400">
            {t('find_guide_santai')}
          </span>

          <div className="grid gap-3">
            <button
              key="google"
              onClick={loginWithGoogle}
              type="button"
              className="nc-will-change-transform flex w-full rounded-lg bg-blue-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
            >
              {loading !== 'google' ? (
                <div className="flex w-full items-center">
                  <img
                    className="flex-shrink-0"
                    src={googleSvg}
                    alt="Continue with google"
                  />
                  <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                    {t('continue_google')}
                  </h3>
                </div>
              ) : (
                <div className="flex w-full justify-center">
                  <Loader
                    type="Circles"
                    color="#d24e29"
                    height={25}
                    width={25}
                  />
                </div>
              )}
            </button>

            <button
              key="facebook"
              onClick={loginWithFacebook}
              type="button"
              className="nc-will-change-transform flex w-full rounded-lg bg-blue-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
            >
              {loading !== 'facebook' ? (
                <div className="flex w-full items-center">
                  <img
                    className="flex-shrink-0"
                    src={facebookSvg}
                    alt="Continue with facebook"
                  />
                  <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                    {t('continue_facebook')}
                  </h3>
                </div>
              ) : (
                <div className="flex w-full justify-center">
                  <Loader
                    type="Circles"
                    color="#d24e29"
                    height={25}
                    width={25}
                  />
                </div>
              )}
            </button>
          </div>

          {/* OR Divider */}
          <div className="relative text-center">
            <span className="relative z-10 inline-block px-4 font-medium text-xs text-neutral-500 dark:text-neutral-400 bg-white dark:bg-neutral-900 uppercase tracking-wider">
              {t('OR') || 'ATAU'}
            </span>
            <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border-t border-neutral-200 dark:border-neutral-700"></div>
          </div>

          {/* Email & Password Form */}
          <form className="grid grid-cols-1 gap-6" onSubmit={loginWithEmail}>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200 font-medium text-sm">
                {t('mail_addr') || 'Alamat E-mel'}
              </span>
              <Input
                type="email"
                placeholder="example@example.com"
                className="mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label className="block">
              <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200 font-medium text-sm">
                {t('password') || 'Kata Laluan'}
              </span>
              <Input
                type="password"
                className="mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <ButtonPrimary type="submit" className="w-full">
              {loading === 'email' ? (
                <Loader
                  type="Circles"
                  color="#ffffff"
                  height={25}
                  width={25}
                />
              ) : (
                t('login') || 'Log Masuk'
              )}
            </ButtonPrimary>
          </form>

          <span className="block text-center text-sm text-neutral-700 dark:text-neutral-300">
            {t('dont_have_account') || 'Belum mempunyai akaun?'}{' '}
            <Link to="/sign-up" className="font-semibold text-primary-6000 hover:underline">
              {t('sign_up') || 'Daftar'}
            </Link>
          </span>

          <h4 className="text-center">
            <Badge color="red" name={"Please allow pop up for social login"} />
          </h4>
        </div>
      </div>
    </div>
  );
};

export default PageLogin;